/**
 * Third-party A/B tests shown on the onboarding plan step as "What others
 * tested": an A/B composite (as published by the source, or composed from the
 * source's own control and variant images), the change in one line, and the
 * reported result. Sources so far: abtest.design, AB Test Cases (Highrise, from
 * the Signal v. Noise write-up) and HubSpot's blog.
 *
 * Rules:
 *  - Every entry is a real, public test with its source URL kept here for
 *    provenance. The result string is the source's own figure, never rounded
 *    up or paraphrased. The source is NOT rendered (founder, 2026-09-10).
 *  - The image is the source's A/B composite (their framing, their labels), so
 *    the pair can never be read as the user's own draft or as our customer
 *    proof.
 *  - Four per goal, picked by relevance; the same example may serve more than
 *    one goal.
 */
import type { OnboardingGoalId } from "@/lib/types/onboarding-goal"
import goingImage from "@/assets/onboarding/examples/going.webp"
import githubImage from "@/assets/onboarding/examples/github.webp"
import pinterestImage from "@/assets/onboarding/examples/pinterest.webp"
import houzzImage from "@/assets/onboarding/examples/houzz.webp"
import goldelucksImage from "@/assets/onboarding/examples/goldelucks.webp"
import tmlewinImage from "@/assets/onboarding/examples/tmlewin.webp"
import hubspotAcademyImage from "@/assets/onboarding/examples/hubspot-academy.webp"
import highriseImage from "@/assets/onboarding/examples/highrise.webp"

export type OnboardingProofExample = {
  id: string
  company: string
  /** What they changed, one line. */
  change: string
  /** The reported result, as the source states it. */
  result: string
  image: string
  sourceUrl: string
  source: "abtest.design" | "hubspot" | "abtestcases"
}

export const ONBOARDING_PROOF_EXAMPLES: Record<string, OnboardingProofExample> = {
  going: {
    id: "going",
    company: "Going",
    change: 'Changed the homepage CTA from "Sign up for free" to "Trial for free"',
    result: "+104% trial starts",
    image: goingImage,
    sourceUrl: "https://abtest.design/tests/trial-for-free-cta",
    source: "abtest.design",
  },
  github: {
    id: "github",
    company: "GitHub",
    change: "More contrast between pricing tiers, leading with the features people value",
    result: "+20% free and paid signups",
    image: githubImage,
    sourceUrl: "https://abtest.design/tests/visual-contrast-between-pricing-tiers",
    source: "abtest.design",
  },
  pinterest: {
    id: "pinterest",
    company: "Pinterest",
    change: "Let visitors browse three pages before asking them to sign up",
    result: "+15 to 20% signups",
    image: pinterestImage,
    sourceUrl: "https://abtest.design/tests/free-content-exploration-before-signup",
    source: "abtest.design",
  },
  houzz: {
    id: "houzz",
    company: "Houzz",
    change: "A dedicated landing page for paid traffic: no header, one offer",
    result: "+5% free trial starts",
    image: houzzImage,
    sourceUrl: "https://abtest.design/tests/customized-landing-page-for-paid-traffic",
    source: "abtest.design",
  },
  goldelucks: {
    id: "goldelucks",
    company: "Goldelucks",
    change: "Benefit-led descriptions at the top of every product page",
    result: "+31.56% orders",
    image: goldelucksImage,
    sourceUrl: "https://abtest.design/tests/enhancing-product-detail-page",
    source: "abtest.design",
  },
  highrise: {
    id: "highrise",
    company: "Highrise",
    change: "Replaced the long feature page with one customer photo, a quote and one button",
    result: "+102% trial signups",
    image: highriseImage,
    sourceUrl: "https://www.abtestcases.com/testcases/testimonial-redesign/",
    source: "abtestcases",
  },
  hubspotAcademy: {
    id: "hubspotAcademy",
    company: "HubSpot Academy",
    change: "Brighter hero with a typing headline, against the plain control",
    result: "+6% conversion rate",
    image: hubspotAcademyImage,
    sourceUrl: "https://blog.hubspot.com/marketing/a-b-testing-experiments-examples",
    source: "hubspot",
  },
  tmlewin: {
    id: "tmlewin",
    company: "T.M. Lewin",
    change: "Put the easy-returns policy next to the size picker",
    result: "+50% conversion rate",
    image: tmlewinImage,
    sourceUrl: "https://abtest.design/tests/clear-transparent-returns-policy",
    source: "abtest.design",
  },
}

const BY_GOAL: Record<OnboardingGoalId, string[]> = {
  saas: ["going", "highrise", "github", "pinterest"],
  ecommerce: ["goldelucks", "tmlewin", "going", "github"],
  newsletter: ["pinterest", "going", "highrise", "hubspotAcademy"],
  agency: ["houzz", "highrise", "going", "github"],
  express: ["going", "highrise", "github", "pinterest"],
}

/** Four examples for a goal, in display order. Never fewer, never duplicates. */
export function examplesForGoal(goal: OnboardingGoalId | null | undefined): OnboardingProofExample[] {
  const ids = BY_GOAL[goal ?? "express"] ?? BY_GOAL.express
  return ids.map((id) => ONBOARDING_PROOF_EXAMPLES[id]).filter(Boolean)
}
